/**
 * Per-APP module entitlement, shared across the whole federation.
 *
 * ── Why this is not `permission-store.disabledModules` ─────────────────────
 * That field is a FLAT list written by whichever remote last hydrated the
 * permission matrix. It answers "which modules are off?" without saying which
 * app they belong to — and `AppModule.code` is unique only WITHIN an app, with
 * several codes deliberately repeating across the catalog (`spark` in all four
 * apps, `settings` too, `users_roles` in three, `reports` / `approvals` /
 * `activity` in two). A flat list therefore cannot tell "Spark is off for
 * Projects" from "Spark is off for Support", which is exactly the distinction
 * the Spark button needs.
 *
 * This store keeps the map /me already returns, keyed by APP CODE, so every
 * consumer asks a question that has one correct answer.
 *
 * ── A DENY-list, and it fails OPEN ─────────────────────────────────────────
 * Nothing published yet (cold boot, /me in flight, an older backend) means
 * HIDE NOTHING — same posture as the backend gate, which is authoritative
 * either way. Hiding a control the workspace paid for because a bootstrap call
 * has not landed is worse than briefly showing one it did not.
 *
 * The shell publishes; remotes read. `@clarix/rbac` is a Module-Federation
 * singleton (tools/mf/shared.ts), so there is one instance of this store for
 * the host and every remote it mounts.
 */

import { create } from 'zustand';

export interface AppModulesState {
  /** True once the shell has published a map at least once. */
  published: boolean;
  /**
   * APP CODE (upper-case, e.g. `PROJECTS`) → `AppModule.code`s this workspace is
   * NOT entitled to. Straight from /me's `active_workspace_apps[CODE]
   * .disabled_modules`.
   */
  disabledByApp: Record<string, readonly string[]>;
}

export const useAppModulesStore = create<AppModulesState>(() => ({
  published: false,
  disabledByApp: {},
}));

/** Uppercase + trim, so callers may pass a slug (`support`) or a code (`SUPPORT`). */
function normalizeAppCode(app: string): string {
  return app.trim().toUpperCase();
}

/**
 * Publish the whole map, replacing what was there.
 *
 * A full replace, never a merge: /me is the complete picture of what this
 * workspace holds, and a merge would leave a re-granted module looking disabled
 * until the tab reloaded.
 */
export function publishAppModules(
  map: Record<string, { disabled_modules?: readonly string[] } | undefined> | null | undefined,
): void {
  const disabledByApp: Record<string, readonly string[]> = {};
  for (const [code, entry] of Object.entries(map ?? {})) {
    disabledByApp[normalizeAppCode(code)] = entry?.disabled_modules ?? [];
  }
  useAppModulesStore.setState({ published: true, disabledByApp });
}

/** Drop everything back to "hide nothing", on sign-out or workspace switch. */
export function clearAppModules(): void {
  useAppModulesStore.setState({ published: false, disabledByApp: {} });
}

/** Imperative read, for code that is not a component. Fails open — see header. */
export function isAppModuleEnabled(app: string, moduleCode: string): boolean {
  const { disabledByApp } = useAppModulesStore.getState();
  const disabled = disabledByApp[normalizeAppCode(app)];
  return !disabled?.includes(moduleCode);
}

/**
 * Whether `moduleCode` is available to this workspace INSIDE `app`.
 *
 * `app` may be a slug or a code. Re-renders when the shell republishes, so a
 * platform admin's toggle reaches an open tab on the next /me heartbeat rather
 * than on the next reload.
 */
export function useAppModuleEnabled(app: string, moduleCode: string): boolean {
  return useAppModulesStore((s) => !s.disabledByApp[normalizeAppCode(app)]?.includes(moduleCode));
}

/** Every module code disabled for `app` — for consumers filtering a list. */
export function useDisabledAppModules(app: string): readonly string[] {
  return useAppModulesStore((s) => s.disabledByApp[normalizeAppCode(app)] ?? EMPTY);
}

/** Stable identity, so `useDisabledAppModules` on an unknown app never loops. */
const EMPTY: readonly string[] = [];
